import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Center,
  Spinner,
  Heading,
  Select,
  Text,
  HStack,
  VStack,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  Card,
  CardBody,
  CardHeader,
  Divider,
  useColorModeValue,
  Progress,
  ButtonGroup,
  Button,
  NumberInput,
  NumberInputField,
  FormControl,
  FormLabel,
  SimpleGrid,
  Icon,
  Flex,
  Badge,
  Container,
  Tooltip,
  CircularProgress,
  CircularProgressLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import {
  parseISO,
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  differenceInCalendarDays,
  isSunday,
} from "date-fns";
import { PieChart, Pie, Cell, Tooltip as ReTooltip } from "recharts";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  FaCalendarAlt,
  FaUser,
  FaChartPie,
  FaCog,
  FaInfoCircle,
  FaCalculator,
  FaSlidersH,
} from "react-icons/fa";
import { FiCheckSquare } from "react-icons/fi";
import { IconWrapper } from "../../../components/IconWrapper";
import { useAuth } from "../../../hooks/useAuth";
import CustomDropdown from "../../../components/CustomDropdown";
import { getThemeClasses } from "../../../utils/themeUtils";
import { useColorMode } from "@chakra-ui/react";
import { showToast } from "../../../utils/toastUtils";
import {
  fetchLeaveEntries,
  fetchLeaveLimits,
  saveLeaveLimits,
  formatNumDays,
  describeDayPortions,
} from "../../../utils/leaveUtils";
import { fetchCheckInRecordsForYear } from "../../../utils/checkInOutUtils";
import {
  computeAccruedLeaveLimits,
  computeLimitAddons,
  applyAddons,
  fetchLeaveAccrualConfig,
  saveLeaveAccrualConfig,
  DEFAULT_ACCRUAL_CONFIG,
  EMPTY_ACCRUAL,
  EMPTY_ADDONS,
  USE_CHECKIN_BASED_ACCRUAL,
} from "../../../utils/leaveAccrualUtils";
import type {
  LeaveAccrual,
  LeaveLimitAddons,
  LeaveAccrualConfig,
} from "../../../utils/leaveAccrualUtils";

interface LeaveRequest {
  id: string;
  userId: string;
  fromDate: string;
  toDate: string;
  leaveType: "Paid" | "Unpaid" | "Comp Off";
  numDays: number;
  reason: string;
  approvalStatus: string;
}

interface Limits {
  paid: number;
  sick: number;
  casual: number;
}

/** Accrual thresholds as typed into the form, before coercion. */
type AccrualDraft = { casual: string; sick: string };

const toAccrualDraft = (config: LeaveAccrualConfig): AccrualDraft => ({
  casual: String(config.workingDaysPerCasualLeave),
  sick: String(config.workingDaysPerSickLeave),
});

interface LeaveManagementProps {
  organizationId: string;
  userId: string;
  userName: string;
  /** Optional list of users — when provided, an admin user picker appears after the year dropdown */
  users?: Array<{ id: string; firstName?: string; lastName?: string }>;
  /** Optional back navigation callback */
  onBack?: () => void;
}

const DEFAULT_LIMITS: Limits = {
  paid: 24,
  sick: 12,
  casual: 12,
};

const COLORS = ["#B59143", "#2ed573"]; // Gold for "Taken", Green for "Remaining"

// Colors for each leave type
const LEAVE_COLORS: Record<"paid" | "sick" | "casual", string> = {
  paid: "#7C3AED", // purple
  sick: "#FBBF24", // yellow
  casual: "#2DD4BF", // teal
};

const LeaveManagement: React.FC<LeaveManagementProps> = ({
  organizationId,
  userId,
  userName,
  users,
  onBack,
}: LeaveManagementProps) => {
  // Internal user selection — defaults to the prop, but can be overridden by the admin picker
  const [activeUserId, setActiveUserId] = React.useState(userId);
  const [activeUserName, setActiveUserName] = React.useState(userName);
  // Sync if parent prop changes
  React.useEffect(() => { setActiveUserId(userId); setActiveUserName(userName); }, [userId, userName]);
  const { user: authUser } = useAuth();
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [historyEntries, setHistoryEntries] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [tab, setTab] = useState<"leave" | "limit">("leave");
  const [currentLimits, setCurrentLimits] = useState<Limits>(DEFAULT_LIMITS);
  const [draftLimits, setDraftLimits] = useState<Limits>(DEFAULT_LIMITS);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showHistory, setShowHistory] = useState(false);
  const [joiningDate, setJoiningDate] = useState<string | null>(null);
  // Tracks that the joining-date lookup has finished, so the accrual doesn't
  // run against a not-yet-loaded null and then immediately recompute.
  const [joiningDateResolved, setJoiningDateResolved] = useState(false);
  // A different member means a different joining date — re-resolve it.
  useEffect(() => { setJoiningDateResolved(false); }, [activeUserId]);
  const [approvedPaidDaysCurrentYear, setApprovedPaidDaysCurrentYear] =
    useState(0);
  const [approvedSickDaysCurrentYear, setApprovedSickDaysCurrentYear] =
    useState(0);
  const [approvedCasualDaysCurrentYear, setApprovedCasualDaysCurrentYear] =
    useState(0);
  const [isEditingLimits, setIsEditingLimits] = useState(false);
  // The limit boxes are edited as raw text so clearing one shows an empty
  // field instead of snapping to 0. null means "not being typed in — mirror
  // draftLimits". The numeric draft follows along, reading empty as 0.
  const [limitText, setLimitText] = useState<{ sick: string; casual: string } | null>(null);

  const [compOffAvailable, setCompOffAvailable] = useState<number>(0);
  const [compOffLoading, setCompOffLoading] = useState<boolean>(false);
  const [compOffEnabled, setCompOffEnabled] = useState<boolean>(false);

  const [canEdit, setCanEdit] = useState(false);
  // The accrual rule is org-wide policy, so it is admin-only — but "admin"
  // means the organization owner OR anyone granted admin permission, not just
  // someone whose role string happens to be "admin".
  const [canEditAccrualRule, setCanEditAccrualRule] = useState(false);

  const handleLimitInput = (field: "sick" | "casual", raw: string) => {
    if (!canEdit) return;
    // Whole days only; an empty box is allowed while typing.
    if (raw !== "" && !/^\d{0,4}$/.test(raw)) return;

    setLimitText((prev) => ({
      sick: field === "sick" ? raw : prev?.sick ?? String(draftLimits.sick),
      casual: field === "casual" ? raw : prev?.casual ?? String(draftLimits.casual),
    }));

    setDraftLimits((d) => {
      const next = { ...d, [field]: raw === "" ? 0 : Number(raw) };
      return { ...next, paid: next.sick + next.casual };
    });
  };

  // Check-in based accrual (26 working days -> 1 casual, 30 -> 1 sick)
  const [accrual, setAccrual] = useState<LeaveAccrual>(EMPTY_ACCRUAL);
  const [accrualLoading, setAccrualLoading] = useState(false);
  // Extra days granted by an admin on top of the accrual, for this year only.
  const [addons, setAddons] = useState<LeaveLimitAddons>(EMPTY_ADDONS);
  // Org-wide accrual thresholds (working days per casual / per sick leave).
  const [accrualConfig, setAccrualConfig] = useState<LeaveAccrualConfig>(DEFAULT_ACCRUAL_CONFIG);
  const [accrualConfigLoaded, setAccrualConfigLoaded] = useState(false);
  const [isAccrualModalOpen, setIsAccrualModalOpen] = useState(false);
  // Held as text for the same reason as limitText: an emptied box should read
  // empty, not silently snap back to 1. Coerced on save, where
  // sanitizeAccrualConfig turns anything unusable into the default.
  const [accrualDraft, setAccrualDraft] = useState<AccrualDraft>(
    toAccrualDraft(DEFAULT_ACCRUAL_CONFIG)
  );
  // Hover-only tooltip: Chakra also opens on focus, and clicking the button
  // focuses it, which left the tip stuck open until you clicked elsewhere.
  const [isAccrualTipOpen, setIsAccrualTipOpen] = useState(false);
  const [isSavingAccrualConfig, setIsSavingAccrualConfig] = useState(false);

  // Load the org's accrual thresholds once per organization.
  useEffect(() => {
    let cancelled = false;
    fetchLeaveAccrualConfig(organizationId).then((cfg) => {
      if (cancelled) return;
      setAccrualConfig(cfg);
      setAccrualDraft(toAccrualDraft(cfg));
      setAccrualConfigLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const handleSaveAccrualConfig = async () => {
    // The Save button is disabled without permission; this guards the path
    // anyway so a stray call can't attempt a write that the rules would reject.
    if (!canEditAccrualRule) return;
    setIsSavingAccrualConfig(true);
    try {
      const saved = await saveLeaveAccrualConfig(organizationId, {
        workingDaysPerCasualLeave: Number(accrualDraft.casual),
        workingDaysPerSickLeave: Number(accrualDraft.sick),
      });
      setAccrualConfig(saved);
      setAccrualDraft(toAccrualDraft(saved));
      setIsAccrualModalOpen(false);
      showToast({
        title: "Accrual rule updated",
        description: `1 casual per ${saved.workingDaysPerCasualLeave} working days, 1 sick per ${saved.workingDaysPerSickLeave}.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Error saving accrual config:", err);
      showToast({
        title: "Error saving accrual rule",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingAccrualConfig(false);
    }
  };

  // 🔒 Check if the current viewer has permission to edit limits
  useEffect(() => {
    const checkRole = async () => {
      if (!authUser) {
        setCanEdit(false);
        return;
      }

      try {
        const orgSnap = await getDoc(doc(db, `organization/${organizationId}`));
        if (orgSnap.exists()) {
          const orgData = orgSnap.data();
          const { members = [] } = orgData;
          const currentViewer = members.find((m: any) => m.id === authUser.uid);
          const normalize = (raw?: string) =>
            (raw || "").toLowerCase().replace(/\s+/g, "");
          const role = normalize(currentViewer?.role);
          const permissions = normalize(currentViewer?.permissions);

          // Mirrors resolveRole() in utils/permissions: the organization owner
          // (organization.admin) and anyone holding admin permission both
          // count as an administrator, alongside the plain admin role. Reading
          // only `role` locks out permission-based admins.
          const isOrgAdmin = !!orgData.admin && orgData.admin === authUser.uid;
          const isAdministrator =
            isOrgAdmin || permissions === "admin" || role === "admin";

          // Per-member leave limits: administrators, plus the managerial roles
          // that have always been able to set them.
          const privilegedRoles = ["manager", "managingdirector"];
          setCanEdit(isAdministrator || privilegedRoles.includes(role));

          // The accrual rule is org-wide policy — administrators only.
          setCanEditAccrualRule(isAdministrator);
        } else {
          setCanEdit(false);
          setCanEditAccrualRule(false);
        }
      } catch (err) {
        console.error("Error checking user role:", err);
        setCanEdit(false);
        setCanEditAccrualRule(false);
      }
    };

    checkRole();
  }, [authUser, organizationId]);

  // Users without privilege can open the Configure tab, but inputs will be read-only.

  // Fetch leave requests and limits
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [entries, limits] = await Promise.all([
          fetchLeaveEntries(organizationId, activeUserId, year),
          fetchLeaveLimits(organizationId, activeUserId),
        ]);

        if (entries.length > 0) {
          const allLeaves = entries
            .filter((e: any) => {
              const status = (e.approvalStatus || "").toLowerCase();
              return (
                status === "approved" ||
                status === "approve" ||
                status === "rejected" ||
                status === "reject" ||
                status === "pending"
              );
            })
            .map((e: any) => ({
              ...e,
              numDays: Number(e.numDays),
            }));
          console.log("All leave requests (approved, rejected, and pending):", allLeaves);
          setRequests(allLeaves);
          setHistoryEntries(allLeaves);
        } else {
          console.log("No leave requests found for user");
          setRequests([]);
          setHistoryEntries([]);
        }

        // Stored limits belong to the year they were saved for. Viewing a
        // different year (or rolling into a new one) starts again from 0.
        // (limitYear is absent on legacy docs — those are still honoured.)
        const limitsAreForThisYear =
          !!limits && (limits.limitYear === undefined || limits.limitYear === year);

        if (limits && limitsAreForThisYear) {
          setCurrentLimits({ paid: limits.paidLimit, sick: limits.sickLimit, casual: limits.casualLimit });
          setDraftLimits({ paid: limits.paidLimit, sick: limits.sickLimit, casual: limits.casualLimit });
        } else {
          // No custom limits for this year, so effectiveTotals uses accruals
          setCurrentLimits({ paid: 0, sick: 0, casual: 0 }); // Placeholder
        }

        // Add-ons expire per year too — a bonus granted in one year does not
        // carry into the next.
        const addonsAreForThisYear =
          !!limits && (limits.addonYear === undefined || limits.addonYear === year);
        setAddons(
          limits && addonsAreForThisYear
            ? {
                paid: limits.paidAddon || 0,
                sick: limits.sickAddon || 0,
                casual: limits.casualAddon || 0,
              }
            : EMPTY_ADDONS
        );

        // Fetch joining date from organization members and comp off setting
        let isCompOffEnabled = true;
        try {
          const orgSnap = await getDoc(doc(db, `organization/${organizationId}`));
          if (orgSnap.exists()) {
            const orgData = orgSnap.data();
            const { members = [] } = orgData;
            const currentMember = members.find((m: any) => m.id === activeUserId);
            const memberJoining =
              currentMember?.dateOfJoining || currentMember?.joiningDate || null;
            setJoiningDate(memberJoining || null);
            // Fetch comp off enabled setting (default: true)
            isCompOffEnabled = orgData.compOffEnabled !== false;
            setCompOffEnabled(isCompOffEnabled);
          } else {
            setJoiningDate(null);
          }
        } catch (err) {
          console.error("Error fetching joining date:", err);
          setJoiningDate(null);
        } finally {
          setJoiningDateResolved(true);
        }

        // Compute comp-off availability if enabled
        if (isCompOffEnabled) {
          await computeCompOffAvailability(organizationId, activeUserId);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        showToast({
          title: "Error loading data",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId, activeUserId, year]);

  // Compute approved usage for the current year
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const approvedEntries = historyEntries.filter((entry: LeaveRequest) => {
      const status = entry.approvalStatus?.toLowerCase();
      const isApproved =
        status === "approved" ||
        status === "approve" ||
        entry.approvalStatus === "Approved" ||
        entry.approvalStatus === "Approve";
      const from = parseISO(entry.fromDate);
      return isApproved && from.getFullYear() === currentYear;
    });

    let sick = 0;
    let casual = 0;
    let paid = 0;

    approvedEntries.forEach((entry: LeaveRequest) => {
      const days = Number(entry.numDays) || 0;
      const reason = entry.reason?.toLowerCase();
      const leaveType = entry.leaveType?.toLowerCase();

      if (reason === "comp off") {
        return;
      }

      if (reason === "sick") {
        sick += days;
      }
      if (reason === "casual") {
        casual += days;
      }
      if (leaveType === "paid") {
        paid += days;
      }
    });

    // Align paid usage with tracked sick/casual days
    const paidTotal = Math.max(paid, sick + casual);

    setApprovedSickDaysCurrentYear(sick);
    setApprovedCasualDaysCurrentYear(casual);
    setApprovedPaidDaysCurrentYear(paidTotal);
  }, [historyEntries]);

  const parsedJoiningDate = useMemo(() => {
    if (!joiningDate) {
      return new Date(year, 0, 1);
    }
    // Handle Firebase Timestamp or String
    let dateObj: Date;
    if (typeof joiningDate === 'object' && (joiningDate as any).seconds) {
      dateObj = new Date((joiningDate as any).seconds * 1000);
    } else {
      dateObj = new Date(joiningDate);
    }
    
    if (isNaN(dateObj.getTime())) {
      return new Date(year, 0, 1);
    }
    return dateObj;
  }, [joiningDate, year]);

  const daysSinceJoining = useMemo(() => {
    if (!parsedJoiningDate) return null;
    return differenceInCalendarDays(new Date(), parsedJoiningDate);
  }, [parsedJoiningDate]);

  const isLockout = useMemo(() => {
    if (!parsedJoiningDate || daysSinceJoining === null) return false;
    return daysSinceJoining < 20;
  }, [parsedJoiningDate, daysSinceJoining]);

  const effectiveTotals = useMemo(() => {
    // 0. TEMPORARY: while the check-in accrual is enabled it is authoritative —
    //    1 casual per 26 working days, 1 sick per 30 working days since joining.
    if (USE_CHECKIN_BASED_ACCRUAL) {
      // Accrued days plus whatever an admin granted on top in Configuration.
      return applyAddons(accrual, addons);
    }

    // 1. If we have non-zero custom limits set in Firestore, use them.
    if (currentLimits.paid > 0 || currentLimits.sick > 0 || currentLimits.casual > 0) {
      return currentLimits;
    }

    // 2. Otherwise calculate automatic accruals (1 Sick + 1 Casual per month)
    const today = new Date();
    const currYear = today.getFullYear();
    const currMonth = today.getMonth(); // 0-based index

    let accruedMonths = 0;
    if (year < currYear) {
      accruedMonths = 12;
    } else if (year === currYear) {
      accruedMonths = currMonth + 1; // e.g., April (3) results in 4 months
    }

    // Adjust for joining date within the same year
    if (parsedJoiningDate) {
      const joiningYear = parsedJoiningDate.getFullYear();
      const joiningMonth = parsedJoiningDate.getMonth();

      if (joiningYear === year) {
        // If joined in March (index 2), we only count March and April (4 - 2 = 2)
        accruedMonths = Math.max(0, accruedMonths - joiningMonth);
      } else if (joiningYear > year) {
        accruedMonths = 0;
      }
    }

    return {
      paid: accruedMonths * 2,
      sick: accruedMonths,
      casual: accruedMonths,
    };
  }, [year, joiningDate, parsedJoiningDate, currentLimits, accrual, addons]);

  // Recompute the check-in accrual whenever the selected member or year
  // changes. Accrual is scoped to one calendar year, so the totals restart
  // from 0 for each new year.
  useEffect(() => {
    if (!USE_CHECKIN_BASED_ACCRUAL) return;
    if (!organizationId || !activeUserId) {
      setAccrual({ ...EMPTY_ACCRUAL, year });
      return;
    }
    // Wait for the lookup, but don't require a value — accounts created before
    // joiningDate existed (notably the founding admin) simply accrue from 1 Jan.
    if (!joiningDateResolved) return;
    // Thresholds drive the result, so don't compute against the defaults first.
    if (!accrualConfigLoaded) return;

    let cancelled = false;
    setAccrualLoading(true);
    computeAccruedLeaveLimits(organizationId, activeUserId, joiningDate, {
      year,
      config: accrualConfig,
    })
      .then((result) => {
        if (!cancelled) setAccrual(result);
      })
      .finally(() => {
        if (!cancelled) setAccrualLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, activeUserId, joiningDate, joiningDateResolved, year, accrualConfig, accrualConfigLoaded]);

  // Keep the Configuration tab's draft values in step with the effective
  // totals. With the check-in accrual on, the accrued numbers always drive the
  // form (unless an admin is mid-edit); otherwise the previous rule applies and
  // stored custom limits are left untouched.
  useEffect(() => {
    if (!isEditingLimits) setLimitText(null);
    if (USE_CHECKIN_BASED_ACCRUAL) {
      if (!isEditingLimits) setDraftLimits(effectiveTotals);
      return;
    }
    if (currentLimits.paid === 0 && currentLimits.sick === 0 && currentLimits.casual === 0) {
      setDraftLimits(effectiveTotals);
    }
  }, [effectiveTotals, currentLimits, activeUserId, year, isEditingLimits]);

  // Compute comp-off availability from Sunday/holiday check-ins (current year)
  const computeCompOffAvailability = async (
    orgId: string,
    uId: string
  ) => {
    if (!orgId || !uId) return;
    try {
      setCompOffLoading(true);
      const currentYear = new Date().getFullYear();
      // Holidays map
      const holidaysCol = collection(
        db,
        "organization",
        orgId,
        "holidays"
      );
      const holidaysSnap = await getDocs(holidaysCol);
      const holidaySet = new Set<string>();
      holidaysSnap.forEach((d) => {
        const data: any = d.data();
        if (data?.date) {
          const key = typeof data.date === "string" ? data.date : String(data.date);
          holidaySet.add(key);
        }
      });

      const checksSnap = await fetchCheckInRecordsForYear(orgId, uId, currentYear);

      const dates = checksSnap
        .map((d: any) => d.checkInTime as string)
        .filter(Boolean)
        .map((s) => {
          try {
            return parseISO(s);
          } catch {
            return null;
          }
        })
        .filter((dt): dt is Date => dt instanceof Date && !isNaN(dt.getTime()));

      // Unique by date within current year
      const dateKeys = new Set<string>();
      dates.forEach((dt) => {
        if (dt.getFullYear() === currentYear) {
          dateKeys.add(format(dt, "yyyy-MM-dd"));
        }
      });

      let count = 0;
      dateKeys.forEach((key) => {
        const dt = parseISO(key);
        if (isNaN(dt.getTime())) return;
        if (isSunday(dt) || holidaySet.has(key)) count += 1;
      });

      setCompOffAvailable(count);
    } catch (err) {
      console.error("Failed to compute comp-off availability", err);
      setCompOffAvailable(0);
    }
    finally {
      setCompOffLoading(false);
    }
  };

  // Stats calculation
  const stats = useMemo(() => {
    const filterYear = year; // Respect the selected year dropdown
    const approvedEntries = historyEntries.filter((r: LeaveRequest) => {
      const status = r.approvalStatus?.toLowerCase();
      const isApproved =
        status === "approved" ||
        status === "approve" ||
        r.approvalStatus === "Approved" ||
        r.approvalStatus === "Approve";
      const d = parseISO(r.fromDate);
      return isApproved && d.getFullYear() === filterYear;
    });

    const pendingEntries = historyEntries.filter((r: LeaveRequest) => {
      const status = r.approvalStatus?.toLowerCase();
      const d = parseISO(r.fromDate);
      return status === "pending" && d.getFullYear() === filterYear;
    });

    const pendingSick = pendingEntries
      .filter((r: LeaveRequest) => r.reason?.toLowerCase() === "sick")
      .reduce((s: number, r: LeaveRequest) => s + r.numDays, 0);

    const pendingCasual = pendingEntries
      .filter((r: LeaveRequest) => r.reason?.toLowerCase() === "casual")
      .reduce((s: number, r: LeaveRequest) => s + r.numDays, 0);

    const pendingPaid = pendingEntries
      .filter((r: LeaveRequest) => r.leaveType?.toLowerCase() === "paid")
      .reduce((s: number, r: LeaveRequest) => s + r.numDays, 0);

    const pendingCompOff = pendingEntries
      .filter((r: LeaveRequest) => {
        const reason = r.reason?.toLowerCase();
        return reason === "comp off" || reason === "compoff" || reason === "comp-off";
      })
      .reduce((s: number, r: LeaveRequest) => s + r.numDays, 0);

    const pendingUnpaid = pendingEntries
      .filter((r: LeaveRequest) => r.leaveType?.toLowerCase() === "unpaid")
      .reduce((s: number, r: LeaveRequest) => s + r.numDays, 0);

    const unpaidLeaves = approvedEntries.filter(
      (r: LeaveRequest) => r.leaveType === "Unpaid"
    );

    const compOffTaken = approvedEntries
      .filter((r: LeaveRequest) => r.reason === "Comp Off" || r.reason === "CompOff" || r.reason === "Comp-Off")
      .reduce((s: number, r: LeaveRequest) => s + r.numDays, 0);

    const paidTakenRaw = approvedEntries
      .filter((r: LeaveRequest) => r.leaveType === "Paid")
      .reduce((s: number, r: LeaveRequest) => s + r.numDays, 0);

    const paidTaken = Math.max(
      paidTakenRaw - compOffTaken,
      approvedPaidDaysCurrentYear
    );

    const sickTaken = approvedEntries
      .filter((r: LeaveRequest) => r.reason === "Sick")
      .reduce((s: number, r: LeaveRequest) => s + r.numDays, 0);

    const casualTaken = approvedEntries
      .filter((r: LeaveRequest) => r.reason === "Casual")
      .reduce((s: number, r: LeaveRequest) => s + r.numDays, 0);

    const stats = {
      totalTaken: approvedEntries.reduce(
        (sum: number, r: LeaveRequest) => sum + r.numDays,
        0
      ),
      paidTaken,
      sickTaken,
      casualTaken,
      compOffTaken,
      unpaidTaken: unpaidLeaves.reduce((s: number, r: LeaveRequest) => s + r.numDays, 0),
      pendingSick,
      pendingCasual,
      pendingPaid,
      pendingUnpaid,
      pendingCompOff,
      remaining: 0, // Calculated more precisely below
    };

    return stats;
  }, [historyEntries, approvedPaidDaysCurrentYear]);

  // Remaining calculation (aligning logic to use effectiveTotals)
  const remainingSick = useMemo(() => {
    return Math.max(0, effectiveTotals.sick - stats.sickTaken - stats.pendingSick);
  }, [effectiveTotals.sick, stats.sickTaken, stats.pendingSick]);

  const remainingCasual = useMemo(() => {
    return Math.max(0, effectiveTotals.casual - stats.casualTaken - stats.pendingCasual);
  }, [effectiveTotals.casual, stats.casualTaken, stats.pendingCasual]);

  const remainingPaid = useMemo(() => {
    return Math.max(0, effectiveTotals.paid - stats.paidTaken - stats.pendingPaid);
  }, [effectiveTotals.paid, stats.paidTaken, stats.pendingPaid]);

  // Handle limit updates
  const handleUpdateLimits = async () => {
    try {
      // Paid limit is always derived from Sick + Casual — never edited directly.
      const computedPaidLimit = draftLimits.sick + draftLimits.casual;

      // What the admin typed is a TOTAL. Store only the part that exceeds the
      // accrual, so the grant keeps applying as more working days accrue.
      const nextAddons = USE_CHECKIN_BASED_ACCRUAL
        ? computeLimitAddons(draftLimits, accrual)
        : EMPTY_ADDONS;

      await saveLeaveLimits(
        organizationId,
        activeUserId,
        {
          paidLimit: computedPaidLimit,
          sickLimit: draftLimits.sick,
          casualLimit: draftLimits.casual,
        },
        // Stamp the year being edited, not "today" — limits expire per year.
        year,
        {
          paidAddon: nextAddons.paid,
          sickAddon: nextAddons.sick,
          casualAddon: nextAddons.casual,
        }
      );

      setAddons(nextAddons);

      const updatedLimits = { ...draftLimits, paid: computedPaidLimit };
      setDraftLimits(updatedLimits);
      setCurrentLimits(updatedLimits);

      const grantedTotal = nextAddons.sick + nextAddons.casual;
      showToast({
        title: "Limits updated",
        description: USE_CHECKIN_BASED_ACCRUAL
          ? grantedTotal > 0
            ? `${nextAddons.casual} casual and ${nextAddons.sick} sick added on top of the ${year} accrual.`
            : `No extra days added — ${year} limits now follow the accrual.`
          : `Year ${year} limits saved.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Error updating limits:", err);
      showToast({
        title: "Error updating limits",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      // Only leave edit mode once the save has settled — dropping out early
      // lets the sync effect repaint the pre-save numbers for a frame.
      setIsEditingLimits(false);
    }
  };

  // Function to generate monthly leave data
  const getMonthlyLeaveData = useMemo(() => {
    const currentDate = selectedMonth;
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);

    // Get all days in the month
    const daysInMonth = eachDayOfInterval({ start, end });

    // Create a map of dates to leave counts
    const leaveCounts = new Map();

    // Initialize all days with 0
    daysInMonth.forEach((day: Date) => {
      leaveCounts.set(format(day, "dd"), 0);
    });

    // Count leaves for each day
    historyEntries
      .filter((req: LeaveRequest) => {
        const fromDate = parseISO(req.fromDate);
        const toDate = parseISO(req.toDate);
        return (
          fromDate.getMonth() === currentDate.getMonth() &&
          fromDate.getFullYear() === currentDate.getFullYear()
        );
      })
      .forEach((req: LeaveRequest) => {
        const fromDate = parseISO(req.fromDate);
        const toDate = parseISO(req.toDate);
        const interval = eachDayOfInterval({ start: fromDate, end: toDate });

        interval.forEach((day: Date) => {
          if (day.getMonth() === currentDate.getMonth()) {
            const dayKey = format(day, "dd");
            leaveCounts.set(dayKey, (leaveCounts.get(dayKey) || 0) + 1);
          }
        });
      });

    // Convert to chart data format
    return Array.from(leaveCounts.entries()).map(([day, count]) => ({
      day,
      leaves: count,
    }));
  }, [historyEntries, selectedMonth]);

  // Function to generate yearly leave data
  const getYearlyLeaveData = useMemo(() => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Initialize data for all months with 0
    const monthlyData = months.map((month) => ({
      month,
      leaves: 0,
    }));

    // Count leaves for each month
    historyEntries
      .filter((req: LeaveRequest) => {
        const fromDate = parseISO(req.fromDate);
        return fromDate.getFullYear() === year;
      })
      .forEach((req: LeaveRequest) => {
        const fromDate = parseISO(req.fromDate);
        const toDate = parseISO(req.toDate);
        const interval = eachDayOfInterval({ start: fromDate, end: toDate });

        interval.forEach((day: Date) => {
          if (day.getFullYear() === year) {
            const monthIndex = day.getMonth();
            monthlyData[monthIndex].leaves += 1;
          }
        });
      });

    return monthlyData;
  }, [historyEntries, year]);

  const cardBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const statBg = useColorModeValue("gray.50", "gray.800");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const headingColor = useColorModeValue("gray.800", "white");
  const subText = useColorModeValue("gray.500", "gray.300");
  const gold = "#B59143";
  const goldSoftBg = useColorModeValue("rgba(181,145,67,0.12)", "rgba(181,145,67,0.20)");
  const axisColor = useColorModeValue("#374151", "#E5E7EB");
  const gridColor = useColorModeValue("#E2E8F0", "#374151");
  const isDarkDropdown = useColorModeValue(false, true);
  const cardItemBg = useColorModeValue("#F9FAFB", "gray.800");
  const progressTrackColor = useColorModeValue("#E5E7EB", "#374151");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.800");
  const tableRowEvenBg = useColorModeValue("gray.50", "gray.800");

  const { colorMode: effectiveColorMode } = useColorMode();
  const isDarkTheme = effectiveColorMode === "dark";
  const theme = getThemeClasses(isDarkTheme);

  if (loading) {
    return (
      <Center py={10}>
        <Spinner size="xl" color="yellow.500" thickness="4px" />
      </Center>
    );
  }

  if (isLockout) {
    return (
      <Box
        w="full"
        position={"relative"}
        px={1}
        paddingTop={2}
        pb={4}
      >
        <Card bg={cardBg}>
          <CardBody>
            <Center py={10}>
              <Text fontSize="lg" fontWeight="semibold" color={headingColor}>
                You are not qualified to take leave as per company guidelines.
              </Text>
            </Center>
          </CardBody>
        </Card>
      </Box>
    );
  }

  const chartData = [
    { name: "Taken", value: stats.totalTaken },
    { name: "Remaining", value: stats.remaining },
  ];

  return (
    <Box
      w="full"
      position={"relative"}
      px={1}
      paddingTop={2}
      pb={4}
    >
      <div className={`flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border}`}>
        {isDarkTheme && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
            <span className={theme.headerOverlaySweep} />
          </>
        )}
        <div className="flex flex-col space-y-1">
          <span className={`text-2xl font-bold ${theme.text}`}>
            Leave Management
          </span>
          <span className={`text-xs ${theme.textMuted}`}>
            Track and configure employee leave balances and statistics
          </span>
        </div>

        {/* Tab Switcher in Header */}
        <div
          role="tablist"
          aria-label="View type"
          className={`relative grid grid-cols-2 ${isDarkTheme ? 'bg-[#14161B] border-[#2E2F33]' : 'bg-white/80 border-gray-200'
            } border rounded-full p-1 shadow-sm h-10`}
        >
          <span
            className={`absolute top-1 bottom-1 rounded-full transition-all duration-300 ${isDarkTheme ? 'bg-[#2A3441]' : 'bg-[#FFF7E6] border border-[#E8C16C]'
              } shadow-sm`}
            style={
              tab === 'leave'
                ? { left: '0.25rem', right: '50%' }
                : { left: '50%', right: '0.25rem' }
            }
            aria-hidden="true"
          />
          <button
            role="tab"
            aria-selected={tab === 'leave'}
            onClick={() => setTab('leave')}
            className={`relative z-10 flex items-center justify-center text-center px-6 py-1 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${tab === 'leave'
              ? isDarkTheme ? 'text-white' : 'text-gray-900'
              : isDarkTheme ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
              }`}
          >
            Statistics
          </button>
          <button
            role="tab"
            aria-selected={tab === 'limit'}
            onClick={() => setTab('limit')}
            className={`relative z-10 flex items-center justify-center text-center px-6 py-1 rounded-full text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${tab === 'limit'
              ? isDarkTheme ? 'text-white' : 'text-gray-900'
              : isDarkTheme ? 'text-[#9CA3AF] hover:text-[#B59143]' : 'text-gray-500 hover:text-[#B59143]'
              }`}
          >
            Configuration
          </button>
        </div>
      </div>

      <Card bg={cardBg} variant="unstyled" shadow="none">
        <CardHeader py={1}>
          <VStack spacing={2} align="stretch">
            <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
              <HStack spacing={2}>
              </HStack>
            </Flex>

            <HStack
              spacing={2}
              className="bg-transparent"
              p={2}
              borderRadius="lg"
              justify="space-between"
              w="full"
            >
              <HStack spacing={2} >
                <IconWrapper icon={FaCalendarAlt} size={16} className="text-[#B59143]" />
                <CustomDropdown
                  value={String(year)}
                  onChange={(value) => setYear(Number(value))}
                  isDark={isDarkDropdown}
                  options={Array.from({ length: 5 }, (_, i) => {
                    const y = new Date().getFullYear() - i;
                    return { value: String(y), label: String(y) };
                  })}
                  className="w-28 px-2 py-1 text-sm rounded-md border"
                  style={{ height: "32px", minWidth: "88px" }}
                />
                {users && users.length > 0 && (
                  <>
                    <IconWrapper icon={FaUser} size={16} className="text-[#B59143]" />
                    <CustomDropdown
                      value={activeUserId}
                      onChange={(value) => {
                        const u = users.find(u => u.id === value);
                        setActiveUserId(value);
                        setActiveUserName(u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : value);
                      }}
                      isDark={isDarkDropdown}
                      options={users.map(u => ({
                        value: u.id,
                        label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.id,
                      }))}
                      className="w-56 px-2 py-1 text-sm rounded-md border"
                      style={{ height: "32px", minWidth: "220px" }}
                    />
                  </>
                )}
              </HStack>
              {tab === "limit" && (
                <Tooltip
                  label="Configure accrual rule — working days per casual / sick leave"
                  hasArrow
                  // Chakra's default tooltip is dark in both themes; follow the
                  // page instead. The arrow reads its fill from this CSS var.
                  bg={isDarkTheme ? "gray.700" : "white"}
                  color={isDarkTheme ? "white" : "gray.800"}
                  border="1px solid"
                  borderColor={borderColor}
                  boxShadow="md"
                  borderRadius="md"
                  fontSize="xs"
                  px={3}
                  py={2}
                  sx={{ "--popper-arrow-bg": isDarkTheme ? "#2D3748" : "#FFFFFF" }}
                  // Driven by hover alone — see isAccrualTipOpen.
                  isOpen={isAccrualTipOpen}
                >
                  <Button
                    aria-label="Configure accrual rule"
                    size="sm"
                    variant="outline"
                    borderColor={gold}
                    color={gold}
                    h="32px"
                    minW="32px"
                    px={2}
                    onMouseEnter={() => setIsAccrualTipOpen(true)}
                    onMouseLeave={() => setIsAccrualTipOpen(false)}
                    onClick={() => {
                      setAccrualDraft(toAccrualDraft(accrualConfig));
                      setIsAccrualTipOpen(false);
                      setIsAccrualModalOpen(true);
                    }}
                    _hover={{ bg: isDarkTheme ? "whiteAlpha.100" : "#FFF7E6" }}
                  >
                    <Icon as={FaSlidersH as any} />
                  </Button>
                </Tooltip>
              )}
              {tab === "leave" && (
                <HStack spacing={2}>
                  {showHistory ? (
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor={gold}
                      color={gold}
                      onClick={() => setShowHistory(false)}
                    >
                      Back
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor={gold}
                      color={gold}
                      onClick={() => setShowHistory(true)}
                    >
                      History
                    </Button>
                  )}
                </HStack>
              )}
            </HStack>
          </VStack>
        </CardHeader>

        <Divider />

        <CardBody p={3}>
          {tab === "leave" ? (
            <Box>
              {!showHistory ? (
                <>
                  <Grid
                    templateColumns={{ base: "1fr", lg: "42% 53%" }}
                    gap={4}
                  >
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} mt={2}>
                      {[
                        {
                          key: "paid" as const,
                          label: "Paid",
                          total: effectiveTotals.paid,
                          taken: stats.paidTaken,
                          pending: stats.pendingPaid,
                          remaining: remainingPaid,
                        },
                        {
                          key: "sick" as const,
                          label: "Sick",
                          total: effectiveTotals.sick,
                          taken: stats.sickTaken,
                          pending: stats.pendingSick,
                          remaining: remainingSick,
                        },
                        {
                          key: "casual" as const,
                          label: "Casual",
                          total: effectiveTotals.casual,
                          taken: stats.casualTaken,
                          pending: stats.pendingCasual,
                          remaining: remainingCasual,
                        },
                      ].map((item) => {
                        const remaining =
                          typeof item.remaining === "number"
                            ? item.remaining
                            : Math.max(item.total - item.taken, 0);
                        const percent =
                          item.total > 0 ? (remaining / item.total) * 100 : 0;
                        return (
                          <Box
                            key={item.key}
                            bg={cardItemBg}
                            borderRadius="lg"
                            boxShadow="sm"
                            p={3}
                            textAlign="center"
                            minW="150px"
                          >
                            <Text
                              fontSize="md"
                              fontWeight="bold"
                              color={headingColor}
                              mb={2}
                            >
                              {item.label}
                            </Text>
                            <CircularProgress
                              value={percent}
                              size="65px"
                              thickness="10px"
                              color={
                                LEAVE_COLORS[
                                item.key as "paid" | "sick" | "casual"
                                ]
                              }
                              trackColor={progressTrackColor}
                            >
                              <CircularProgressLabel>
                                <Box display="flex" flexDirection="column" alignItems="center">
                                  <Text
                                    fontSize="lg"
                                    fontWeight="bold"
                                    color={headingColor}
                                    lineHeight="1.1"
                                  >
                                    {remaining}
                                  </Text>
                                </Box>
                              </CircularProgressLabel>
                            </CircularProgress>
                            <Text fontSize="xs" fontWeight="medium" color={subText} mt={1}>
                              Remaining
                            </Text>

                            <Box mt={3} pt={2} borderTop="1px" borderColor={borderColor}>
                              <Text fontSize="sm" color={subText}>
                                Total: {item.total}
                              </Text>
                              <Text fontSize="xs" color={subText} mt={0.5}>
                                Taken: {item.taken} | Pending: {item.pending}
                              </Text>
                            </Box>
                          </Box>
                        );
                      })}
                      {/* Unpaid Leaves Card */}
                      <Box
                        bg={cardItemBg}
                        borderRadius="lg"
                        boxShadow="sm"
                        p={3}
                        textAlign="center"
                        minW="150px"
                      >
                        {compOffEnabled && (
                        <Box mt={2}>
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color={headingColor}
                            mb={2}
                          >
                            Comp Off Leaves
                          </Text>
                          <Text fontSize="xs" color={subText}>
                            Available: {compOffLoading ? "..." : compOffAvailable}
                          </Text>
                          <Text fontSize="xs" color={subText}>
                            Taken: {stats.compOffTaken}
                          </Text>
                          <Text fontSize="xs" color={subText}>
                            Pending: {stats.pendingCompOff}
                          </Text>
                        </Box>
                        )}
                        <Box mt={5}>
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color={headingColor}
                            mb={2}
                          >
                            Unpaid Leaves
                          </Text>
                          <Text fontSize="xs" color={subText}>
                            Taken: {stats.unpaidTaken}
                          </Text>
                          <Text fontSize="xs" color={subText}>
                            Pending: {stats.pendingUnpaid}
                          </Text>
                        </Box>
                      </Box>

                    </SimpleGrid>

                    {/* Leave Track Chart */}
                    <Box
                      bg={cardBg}
                      borderRadius="lg"
                      boxShadow="sm"
                      p={4}
                      maxW="100%"
                      overflow="hidden"
                      w="100%"
                      minW="0"
                      ml={5}
                      mt={2}
                      mb={0}
                    >
                      <Text fontSize="lg" fontWeight="semibold" mb={4} color={headingColor}>
                        Leave Track
                      </Text>
                      <Box h="250px" w="100%" maxW="100%" mt={6}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={getYearlyLeaveData}>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              vertical={false}
                              stroke={gridColor}
                            />
                            <XAxis
                              dataKey="month"
                              tick={{ fontSize: 12, fill: axisColor }}
                              tickLine={false}
                              axisLine={{ stroke: gridColor }}
                            />
                            <YAxis
                              tick={{ fontSize: 12, fill: axisColor }}
                              tickLine={false}
                              axisLine={{ stroke: gridColor }}
                              tickFormatter={(value: number) =>
                                value.toString()
                              }
                              domain={[0, 10]}
                              ticks={[0, 2, 4, 6, 8, 10]}
                              label={{
                                value: "No Of Days",
                                angle: -90,
                                position: "insideLeft",
                                offset: 10,
                                dy: 20,
                                style: {
                                  fontSize: "12px",
                                  fill: axisColor,
                                  fontFamily: "inherit",
                                },
                              }}
                            />
                            <RechartsTooltip
                              content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                  return (
                                    <Box
                                      bg={cardBg}
                                      p={3}
                                      borderRadius="md"
                                      boxShadow="lg"
                                      border={`1px solid ${borderColor}`}
                                    >
                                      <Text fontSize="sm" fontWeight="semibold" color={headingColor}>
                                        {label}
                                      </Text>
                                      <Text fontSize="sm" color={gold}>
                                        Total Leaves: {payload[0].value}
                                      </Text>
                                    </Box>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar
                              dataKey="leaves"
                              fill={gold}
                              radius={[4, 4, 0, 0]}
                              name="Total Leaves"
                              barSize={20}
                            />
                            <Legend
                              formatter={(value: string) => (
                                <span style={{ color: headingColor }}>
                                  {value}
                                </span>
                              )}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                </>
              ) : (
                /* Leave History Table */
                <Box bg={cardBg} borderRadius="lg" boxShadow="sm">
                  <Text
                    fontSize="lg"
                    fontWeight="semibold"
                    p={4}
                    borderBottom="1px"
                    borderColor={borderColor}
                  >
                    Leave History
                  </Text>
                  <Box overflowX="auto" maxH="65vh" overflowY="auto" position="relative">
                    <Box as="table" w="full" textAlign="left">
                      <Box as="thead" bg={tableHeaderBg} position="sticky" top={0} zIndex={10}>
                        <Box as="tr">
                          <Box
                            as="th"
                            px={6}
                            py={3}
                            fontSize="sm"
                            fontWeight="medium"
                            color={subText}
                          >
                            Leave Type
                          </Box>
                          <Box
                            as="th"
                            px={6}
                            py={3}
                            fontSize="sm"
                            fontWeight="medium"
                            color={subText}
                          >
                            Reason
                          </Box>
                          <Box
                            as="th"
                            px={6}
                            py={3}
                            fontSize="sm"
                            fontWeight="medium"
                            color={subText}
                          >
                            From Date
                          </Box>
                          <Box
                            as="th"
                            px={6}
                            py={3}
                            fontSize="sm"
                            fontWeight="medium"
                            color={subText}
                          >
                            To Date
                          </Box>
                          <Box
                            as="th"
                            px={6}
                            py={3}
                            fontSize="sm"
                            fontWeight="medium"
                            color={subText}
                          >
                            No. of Days
                          </Box>
                          <Box
                            as="th"
                            px={6}
                            py={3}
                            fontSize="sm"
                            fontWeight="medium"
                            color={subText}
                          >
                            Status
                          </Box>
                        </Box>
                      </Box>
                      <Box as="tbody">
                        {historyEntries
                          .filter(
                            (req: LeaveRequest) =>
                              parseISO(req.fromDate).getFullYear() === year
                          )
                          .map((request: LeaveRequest, idx: number) => (
                            <Box
                              as="tr"
                              key={request.id}
                              bg={idx % 2 === 0 ? cardBg : tableRowEvenBg}
                            >
                              <Box
                                as="td"
                                px={6}
                                py={4}
                                fontSize="sm"
                                color={headingColor}
                              >
                                {request.leaveType}
                              </Box>
                              <Box
                                as="td"
                                px={6}
                                py={4}
                                fontSize="sm"
                                color={headingColor}
                              >
                                {request.reason}
                              </Box>
                              <Box
                                as="td"
                                px={6}
                                py={4}
                                fontSize="sm"
                                color={headingColor}
                              >
                                {new Date(
                                  request.fromDate
                                ).toLocaleDateString()}
                              </Box>
                              <Box
                                as="td"
                                px={6}
                                py={4}
                                fontSize="sm"
                                color={headingColor}
                              >
                                {new Date(request.toDate).toLocaleDateString()}
                              </Box>
                              <Box
                                as="td"
                                px={6}
                                py={4}
                                fontSize="sm"
                                color={headingColor}
                              >
                                <Box
                                  as="span"
                                  title={describeDayPortions(request as any) || undefined}
                                >
                                  {formatNumDays(request.numDays)}
                                </Box>
                              </Box>
                              <Box
                                as="td"
                                px={6}
                                py={4}
                                fontSize="sm"
                                color={headingColor}
                              >
                                <Badge
                                  colorScheme={
                                    request.approvalStatus?.toLowerCase() === "approved" ||
                                      request.approvalStatus?.toLowerCase() === "approve"
                                      ? "green"
                                      : request.approvalStatus?.toLowerCase() === "rejected" ||
                                        request.approvalStatus?.toLowerCase() === "reject"
                                        ? "red"
                                        : "orange"
                                  }
                                  variant="subtle"
                                  borderRadius="md"
                                  px={2}
                                  py={1}
                                  textTransform="capitalize"
                                >
                                  {request.approvalStatus?.toLowerCase()}
                                </Badge>
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box py={4}>
              <VStack spacing={8} align="center" w="full">
                <VStack spacing={2} align="center">
                  <Text fontSize="2xl" fontWeight="black" color={headingColor}>
                    Configure Yearly Leave Limits
                  </Text>
                  <Text fontSize="sm" color={subText} textAlign="center" maxW="500px">
                    Define the number of leave days allowed for each category per year. These limits will apply to all calculations and statistics.
                  </Text>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
                  {/* Paid Leaves Card */}
                  <Box
                    bg={isDarkTheme ? "gray.800" : "white"}
                    p={6}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor={borderColor}
                    boxShadow="sm"
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-4px)", boxShadow: "xl", borderColor: gold }}
                    position="relative"
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      top="-10px"
                      right="-10px"
                      bg={LEAVE_COLORS.paid}
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      opacity={0.1}
                    />
                    <VStack spacing={4} align="center">
                      <Box p={3} borderRadius="xl" bg={`${LEAVE_COLORS.paid}15`}>
                        <Icon as={FaCalculator as any} w={6} h={6} color={LEAVE_COLORS.paid} />
                      </Box>
                      <VStack spacing={1}>
                        <Text fontWeight="bold" fontSize="lg" color={headingColor}>
                          Paid Leaves
                        </Text>
                        <Text fontSize="xs" color={subText}>Annual entitlement</Text>
                      </VStack>
                      <Input
                        type="number"
                        value={draftLimits.sick + draftLimits.casual}
                        isReadOnly
                        variant="unstyled"
                        fontSize="3xl"
                        fontWeight="black"
                        textAlign="center"
                        color={headingColor}
                        _focus={{ color: gold }}
                      />
                      <Box h="1px" w="40%" bg={borderColor} />
                      <Text fontSize="xs" color={subText} fontWeight="medium">DAYS (Sick + Casual)</Text>
                    </VStack>
                  </Box>

                  {/* Sick Leaves Card */}
                  <Box
                    bg={isDarkTheme ? "gray.800" : "white"}
                    p={6}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor={isEditingLimits ? gold : borderColor}
                    boxShadow={isEditingLimits ? "lg" : "sm"}
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-4px)", boxShadow: "xl", borderColor: gold }}
                    position="relative"
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      top="-10px"
                      right="-10px"
                      bg={LEAVE_COLORS.sick}
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      opacity={0.1}
                    />
                    <VStack spacing={4} align="center">
                      <Box p={3} borderRadius="xl" bg={`${LEAVE_COLORS.sick}15`}>
                        <Icon as={FaInfoCircle as any} w={6} h={6} color={LEAVE_COLORS.sick} />
                      </Box>
                      <VStack spacing={1}>
                        <Text fontWeight="bold" fontSize="lg" color={headingColor}>
                          Sick Leaves
                        </Text>
                        <Text fontSize="xs" color={subText}>Medical allowance</Text>
                      </VStack>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={limitText?.sick ?? String(draftLimits.sick)}
                        onChange={(e) => handleLimitInput("sick", e.target.value)}
                        isReadOnly={!isEditingLimits || !canEdit}
                        variant="unstyled"
                        fontSize="3xl"
                        fontWeight="black"
                        textAlign="center"
                        color={headingColor}
                        _focus={{ color: gold }}
                      />
                      <Box h="1px" w="40%" bg={borderColor} />
                      <Text fontSize="xs" color={subText} fontWeight="medium">DAYS</Text>
                    </VStack>
                  </Box>

                  {/* Casual Leaves Card */}
                  <Box
                    bg={isDarkTheme ? "gray.800" : "white"}
                    p={6}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor={isEditingLimits ? gold : borderColor}
                    boxShadow={isEditingLimits ? "lg" : "sm"}
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-4px)", boxShadow: "xl", borderColor: gold }}
                    position="relative"
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      top="-10px"
                      right="-10px"
                      bg={LEAVE_COLORS.casual}
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      opacity={0.1}
                    />
                    <VStack spacing={4} align="center">
                      <Box p={3} borderRadius="xl" bg={`${LEAVE_COLORS.casual}15`}>
                        <Icon as={FaCalendarAlt as any} w={6} h={6} color={LEAVE_COLORS.casual} />
                      </Box>
                      <VStack spacing={1}>
                        <Text fontWeight="bold" fontSize="lg" color={headingColor}>
                          Casual Leaves
                        </Text>
                        <Text fontSize="xs" color={subText}>Personal time off</Text>
                      </VStack>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={limitText?.casual ?? String(draftLimits.casual)}
                        onChange={(e) => handleLimitInput("casual", e.target.value)}
                        isReadOnly={!isEditingLimits || !canEdit}
                        variant="unstyled"
                        fontSize="3xl"
                        fontWeight="black"
                        textAlign="center"
                        color={headingColor}
                        _focus={{ color: gold }}
                      />
                      <Box h="1px" w="40%" bg={borderColor} />
                      <Text fontSize="xs" color={subText} fontWeight="medium">DAYS</Text>
                    </VStack>
                  </Box>
                </SimpleGrid>

                {canEdit && (
                  <Button
                    size="lg"
                    height="56px"
                    px={10}
                    fontSize="md"
                    fontWeight="bold"
                    borderRadius="full"
                    bg={gold}
                    color="white"
                    _hover={{
                      bg: "#9c7c37",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 20px rgba(181, 145, 67, 0.3)"
                    }}
                    _active={{ bg: "#8a6d31", transform: "translateY(0)" }}
                    transition="all 0.2s"
                    onClick={() => {
                      if (!isEditingLimits) {
                        setIsEditingLimits(true);
                      } else {
                        // handleUpdateLimits clears edit mode when it settles.
                        handleUpdateLimits();
                      }
                    }}
                    leftIcon={<Icon as={isEditingLimits ? FiCheckSquare as any : FaCalculator as any} />}
                  >
                    {isEditingLimits ? "Save & Apply Changes" : "Update Leaves"}
                  </Button>
                )}
              </VStack>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Accrual rule configuration — org-wide thresholds */}
      <Modal
        isOpen={isAccrualModalOpen}
        onClose={() => setIsAccrualModalOpen(false)}
        isCentered
        size="md"
      >
        <ModalOverlay />
        <ModalContent bg={isDarkTheme ? "gray.800" : "white"} borderRadius="2xl">
          <ModalHeader color={headingColor}>Leave Accrual Rule</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={5} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color={headingColor}>
                  Working days per Casual leave
                </FormLabel>
                <NumberInput
                  min={1}
                  value={accrualDraft.casual}
                  onChange={(valueAsString) =>
                    setAccrualDraft((d) => ({ ...d, casual: valueAsString }))
                  }
                  isDisabled={!canEditAccrualRule}
                >
                  <NumberInputField placeholder="26" />
                </NumberInput>
                <Text fontSize="xs" color={subText} mt={1}>
                  Default 26 — 1 casual leave per 26 checked-in days.
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" color={headingColor}>
                  Working days per Sick leave
                </FormLabel>
                <NumberInput
                  min={1}
                  value={accrualDraft.sick}
                  onChange={(valueAsString) =>
                    setAccrualDraft((d) => ({ ...d, sick: valueAsString }))
                  }
                  isDisabled={!canEditAccrualRule}
                >
                  <NumberInputField placeholder="30" />
                </NumberInput>
                <Text fontSize="xs" color={subText} mt={1}>
                  Default 30 — 1 sick leave per 30 checked-in days.
                </Text>
              </FormControl>

              {!canEditAccrualRule && (
                <Text fontSize="xs" color="orange.400">
                  Only an organization admin can change this rule.
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              variant="outline"
              onClick={() => setIsAccrualModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              bg={gold}
              color="white"
              _hover={{ bg: "#9c7c37" }}
              isLoading={isSavingAccrualConfig}
              isDisabled={!canEditAccrualRule}
              onClick={handleSaveAccrualConfig}
            >
              Save Rule
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LeaveManagement;